/**
 * PerspectiveWarp.js
 * Performs perspective transformation on images based on detected marker corners
 */

/**
 * Applies perspective warp transformation to correct image distortion
 * @param {HTMLImageElement} image - The source image
 * @param {Array} corners - Array of 4 corner points [{x, y}, {x, y}, {x, y}, {x, y}]
 * @param {number} outputWidth - Width of output image
 * @param {number} outputHeight - Height of output image
 * @returns {HTMLCanvasElement} Canvas with warped image
 */
export function perspectiveWarp(image, corners, outputWidth, outputHeight) {
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d");

  // Define destination corners (rectangle)
  const dstCorners = [
    { x: 0, y: 0 },
    { x: outputWidth, y: 0 },
    { x: outputWidth, y: outputHeight },
    { x: 0, y: outputHeight },
  ];

  // Calculate perspective transform matrix
  const matrix = getPerspectiveTransform(corners, dstCorners);

  // Apply transformation
  applyPerspectiveTransform(ctx, image, matrix, outputWidth, outputHeight);

  return canvas;
}

/**
 * Calculate perspective transformation matrix
 * @param {Array} src - Source points
 * @param {Array} dst - Destination points
 * @returns {Array} 3x3 transformation matrix
 */
function getPerspectiveTransform(src, dst) {
  // Set up the system of equations for perspective transform
  const A = [];
  const b = [];

  for (let i = 0; i < 4; i++) {
    const sx = src[i].x;
    const sy = src[i].y;
    const dx = dst[i].x;
    const dy = dst[i].y;

    A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
    b.push(dx);

    A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
    b.push(dy);
  }

  // Solve the system using Gaussian elimination
  const h = solveLinearSystem(A, b);
  h.push(1); // h8 is always 1

  // Return as 3x3 matrix
  return [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], h[8]],
  ];
}

/**
 * Solve a linear system using Gaussian elimination
 * @param {Array} A - Coefficient matrix
 * @param {Array} b - Result vector
 * @returns {Array} Solution vector
 */
function solveLinearSystem(A, b) {
  const n = A.length;
  const augmented = A.map((row, i) => [...row, b[i]]);

  // Forward elimination
  for (let i = 0; i < n; i++) {
    // Find pivot
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k;
      }
    }

    // Swap rows
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

    // Eliminate column
    for (let k = i + 1; k < n; k++) {
      const factor = augmented[k][i] / augmented[i][i];
      for (let j = i; j <= n; j++) {
        augmented[k][j] -= factor * augmented[i][j];
      }
    }
  }

  // Back substitution
  const solution = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    solution[i] = augmented[i][n];
    for (let j = i + 1; j < n; j++) {
      solution[i] -= augmented[i][j] * solution[j];
    }
    solution[i] /= augmented[i][i];
  }

  return solution;
}

/**
 * Apply perspective transformation to image
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {HTMLImageElement} image - Source image
 * @param {Array} matrix - Transformation matrix
 * @param {number} width - Output width
 * @param {number} height - Output height
 */
function applyPerspectiveTransform(ctx, image, matrix, width, height) {
  // Create temporary canvas to read from
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = image.width;
  tempCanvas.height = image.height;
  const tempCtx = tempCanvas.getContext("2d");
  tempCtx.drawImage(image, 0, 0);
  const srcData = tempCtx.getImageData(0, 0, image.width, image.height);

  // Create output image data
  const dstData = ctx.createImageData(width, height);

  // Invert the matrix to map from destination to source
  const invMatrix = invertMatrix(matrix);

  // For each pixel in destination
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Apply inverse transform to find source coordinates
      const srcCoords = applyMatrix(invMatrix, x, y);
      const sx = srcCoords.x;
      const sy = srcCoords.y;

      // Check if source coordinates are within bounds
      if (sx >= 0 && sx < image.width - 1 && sy >= 0 && sy < image.height - 1) {
        // Bilinear interpolation
        const pixel = bilinearInterpolate(srcData, sx, sy);
        const dstIdx = (y * width + x) * 4;
        dstData.data[dstIdx] = pixel.r;
        dstData.data[dstIdx + 1] = pixel.g;
        dstData.data[dstIdx + 2] = pixel.b;
        dstData.data[dstIdx + 3] = 255;
      }
    }
  }

  ctx.putImageData(dstData, 0, 0);
}

/**
 * Invert a 3x3 matrix
 * @param {Array} m - Input matrix
 * @returns {Array} Inverted matrix
 */
function invertMatrix(m) {
  const det =
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);

  if (Math.abs(det) < 1e-10) {
    throw new Error("Matrix is singular and cannot be inverted");
  }

  const inv = [
    [
      (m[1][1] * m[2][2] - m[1][2] * m[2][1]) / det,
      (m[0][2] * m[2][1] - m[0][1] * m[2][2]) / det,
      (m[0][1] * m[1][2] - m[0][2] * m[1][1]) / det,
    ],
    [
      (m[1][2] * m[2][0] - m[1][0] * m[2][2]) / det,
      (m[0][0] * m[2][2] - m[0][2] * m[2][0]) / det,
      (m[0][2] * m[1][0] - m[0][0] * m[1][2]) / det,
    ],
    [
      (m[1][0] * m[2][1] - m[1][1] * m[2][0]) / det,
      (m[0][1] * m[2][0] - m[0][0] * m[2][1]) / det,
      (m[0][0] * m[1][1] - m[0][1] * m[1][0]) / det,
    ],
  ];

  return inv;
}

/**
 * Apply transformation matrix to a point
 * @param {Array} matrix - 3x3 transformation matrix
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @returns {Object} Transformed coordinates {x, y}
 */
function applyMatrix(matrix, x, y) {
  const w = matrix[2][0] * x + matrix[2][1] * y + matrix[2][2];
  return {
    x: (matrix[0][0] * x + matrix[0][1] * y + matrix[0][2]) / w,
    y: (matrix[1][0] * x + matrix[1][1] * y + matrix[1][2]) / w,
  };
}

/**
 * Bilinear interpolation for smooth pixel sampling
 * @param {ImageData} imgData - Source image data
 * @param {number} x - X coordinate (can be fractional)
 * @param {number} y - Y coordinate (can be fractional)
 * @returns {Object} Interpolated pixel {r, g, b}
 */
function bilinearInterpolate(imgData, x, y) {
  const x1 = Math.floor(x);
  const y1 = Math.floor(y);
  const x2 = x1 + 1;
  const y2 = y1 + 1;

  const fx = x - x1;
  const fy = y - y1;

  const getPixel = (px, py) => {
    const idx = (py * imgData.width + px) * 4;
    return {
      r: imgData.data[idx],
      g: imgData.data[idx + 1],
      b: imgData.data[idx + 2],
    };
  };

  const p11 = getPixel(x1, y1);
  const p21 = getPixel(x2, y1);
  const p12 = getPixel(x1, y2);
  const p22 = getPixel(x2, y2);

  return {
    r: Math.round(
      p11.r * (1 - fx) * (1 - fy) +
        p21.r * fx * (1 - fy) +
        p12.r * (1 - fx) * fy +
        p22.r * fx * fy
    ),
    g: Math.round(
      p11.g * (1 - fx) * (1 - fy) +
        p21.g * fx * (1 - fy) +
        p12.g * (1 - fx) * fy +
        p22.g * fx * fy
    ),
    b: Math.round(
      p11.b * (1 - fx) * (1 - fy) +
        p21.b * fx * (1 - fy) +
        p12.b * (1 - fx) * fy +
        p22.b * fx * fy
    ),
  };
}

/**
 * Order corners in consistent manner (top-left, top-right, bottom-right, bottom-left)
 * @param {Array} corners - Unordered corner points
 * @returns {Array} Ordered corner points
 */
export function orderCorners(corners) {
  // Calculate centroid
  const cx = corners.reduce((sum, c) => sum + c.x, 0) / 4;
  const cy = corners.reduce((sum, c) => sum + c.y, 0) / 4;

  // Sort by angle from centroid
  const sorted = corners
    .map((c) => ({
      ...c,
      angle: Math.atan2(c.y - cy, c.x - cx),
    }))
    .sort((a, b) => a.angle - b.angle);

  // Find top-left (smallest sum of coordinates)
  let minIdx = 0;
  let minSum = sorted[0].x + sorted[0].y;
  sorted.forEach((c, i) => {
    const sum = c.x + c.y;
    if (sum < minSum) {
      minSum = sum;
      minIdx = i;
    }
  });

  // Rotate array so top-left is first, preserve all properties including id
  const ordered = [];
  for (let i = 0; i < 4; i++) {
    const idx = (minIdx + i) % 4;
    ordered.push({ x: sorted[idx].x, y: sorted[idx].y, id: sorted[idx].id });
  }

  return ordered;
}
